/**
 * useGdyniaVehicles.js
 * 
 * Hook React odpowiedzialny za cykliczne pobieranie i aproksymowanie
 * pozycji pojazdów ZKM Gdynia.
 */

import { useState, useEffect, useRef } from 'react';
import {
  getApproximatedGdyniaPositions,
  setKnownGpsVehicleIds
} from '../services/gdyniaPositions';

/**
 * Hook do aproksymacji pozycji gdyńskich pojazdów.
 * 
 * @param {Array|null} userLocation - [lat, lon] pozycja użytkownika lub centrum mapy
 * @param {Array} realGpsVehicles - pojazdy z normalnego GPS (żeby nie duplikować)
 * @param {number} refreshInterval - interwał odświeżania w ms (default 30s)
 * @returns {{ gdyniaVehicles: Array, isLoading: boolean, lastUpdated: Date|null }}
 */
export default function useGdyniaVehicles(userLocation, realGpsVehicles = [], refreshInterval = 30000) {
  const [gdyniaVehicles, setGdyniaVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isMounted = useRef(true);

  // Aktualizuj zbiór vehicleId znanych z GPS
  useEffect(() => {
    if (realGpsVehicles && realGpsVehicles.length > 0) {
      const ids = realGpsVehicles
        .map(v => v.vehicleId)
        .filter(Boolean);
      setKnownGpsVehicleIds(ids);
    }
  }, [realGpsVehicles]);

  // Główna pętla odświeżania
  useEffect(() => {
    isMounted.current = true;

    const fetchPositions = async () => {
      setIsLoading(true);
      try {
        const positions = await getApproximatedGdyniaPositions();
        if (isMounted.current) {
          setGdyniaVehicles(positions);
          setLastUpdated(new Date());
        }
      } catch (e) {
        console.error('[useGdyniaVehicles] Błąd:', e);
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    fetchPositions();
    const interval = setInterval(fetchPositions, refreshInterval);

    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [refreshInterval]);

  return { gdyniaVehicles, isLoading, lastUpdated };
}
